import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { useProfile } from '@/hooks/useProfile';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

interface StudentViewContextType {
  isStudentView: boolean;
  toggleStudentView: () => void;
  exitStudentView: () => void;
  isRecruiterView: boolean;
  toggleRecruiterView: () => void;
  exitRecruiterView: () => void;
  isProfessorView: boolean;
  toggleProfessorView: () => void;
  exitProfessorView: () => void;
  canActivate: boolean;
  canActivateRecruiterView: boolean;
  canActivateProfessorView: boolean;
}

const StudentViewContext = createContext<StudentViewContextType | undefined>(undefined);

export function StudentViewProvider({ children }: { children: ReactNode }) {
  const { profile } = useProfile();
  const { user } = useAuth();
  const [isStudentView, setIsStudentView] = useState(false);
  const [isRecruiterView, setIsRecruiterView] = useState(false);
  const [isProfessorView, setIsProfessorView] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const checkAdmin = async () => {
      if (!user?.id) return;
      const { data } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .eq('role', 'admin')
        .maybeSingle();
      setIsAdmin(!!data);
    };
    checkAdmin();
  }, [user?.id]);

  const canActivate = (isAdmin || profile?.role === 'judge') && profile?.role !== 'recruiter';
  // Only admins can toggle recruiter view (recruiters already have the tab)
  const canActivateRecruiterView = isAdmin;
  const canActivateProfessorView = isAdmin;

  const toggleStudentView = useCallback(() => {
    if (canActivate) {
      setIsStudentView(prev => !prev);
      setIsRecruiterView(false);
      setIsProfessorView(false);
    }
  }, [canActivate]);

  const exitStudentView = useCallback(() => {
    setIsStudentView(false);
  }, []);

  const toggleRecruiterView = useCallback(() => {
    if (canActivateRecruiterView) {
      setIsRecruiterView(prev => !prev);
      setIsStudentView(false);
      setIsProfessorView(false);
    }
  }, [canActivateRecruiterView]);

  const exitRecruiterView = useCallback(() => {
    setIsRecruiterView(false);
  }, []);

  const toggleProfessorView = useCallback(() => {
    if (canActivateProfessorView) {
      setIsProfessorView(prev => !prev);
      setIsStudentView(false);
      setIsRecruiterView(false);
    }
  }, [canActivateProfessorView]);

  const exitProfessorView = useCallback(() => {
    setIsProfessorView(false);
  }, []);

  return (
    <StudentViewContext.Provider value={{
      isStudentView, toggleStudentView, exitStudentView,
      isRecruiterView, toggleRecruiterView, exitRecruiterView,
      isProfessorView, toggleProfessorView, exitProfessorView,
      canActivate, canActivateRecruiterView, canActivateProfessorView,
    }}>
      {children}
    </StudentViewContext.Provider>
  );
}

export function useStudentView() {
  const context = useContext(StudentViewContext);
  if (context === undefined) {
    throw new Error('useStudentView must be used within a StudentViewProvider');
  }
  return context;
}
